import { useEffect, JSX } from 'react';
import { translationClasses } from 'src/shared/TranslationClasses';
import { C40SimpleSignUpProps } from './C40SimpleSignUp.types';
import { CreateSimpleSignUpModel } from './C40SimpleSignUp.service';
import { Form, SimpleSignup } from '@ahc-digital/component-library';
import { GetServerSideComponentProps, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { getDroplink } from 'src/shared/param-utils';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { checkDatasourceForRendering } from '@/shared/checkDatasourceForRendering';

const Default = (props?: C40SimpleSignUpProps): JSX.Element => {
  const { rendering, params, fields, formioProjectUrl, formioScript } = props || {};
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing ?? false;
  const marketingRenderingName = useCreateMarketingRenderingHame(
    rendering?.componentName as string
  );

  useEffect(() => {
    const loadCreateForm = () => {
      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = formioScript as string;
        script.type = 'module';
        script.async = true;

        script.onload = () => {
          if (typeof window.createForm === 'function') {
            resolve();
          } else {
            reject(new Error('createForm is not defined on window'));
          }
        };

        script.onerror = () => {
          reject(new Error('Failed to load the createForm script'));
        };

        document.body.appendChild(script);
      });
    };

    loadCreateForm()
      .then(() => {
        // Now it's safe to use window.createForm
        window.createForm(`${fields?.FormID?.value}`, {
          redirect: `/`,
          formProjectUrl: `${formioProjectUrl}`,
        });
      })
      .catch((error) => {
        console.error(error);
      });

    // Cleanup script on component unmount
    return () => {
      const script = document.querySelector(`script[src="${formioScript}"]`);
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [fields?.FormID?.value, formioProjectUrl, formioScript]);

  if (isEditing) {
    return (
      <SimpleSignup
        {...CreateSimpleSignUpModel(props)}
        className={translationClasses(params)}
        id={params?.AnchorLinkTargetName}
      >
        <p>
          <b>Form Name:</b> <Text field={fields?.FormID} />
        </p>
      </SimpleSignup>
    );
  }

  return (
    <SimpleSignup
      {...CreateSimpleSignUpModel(props)}
      className={translationClasses(params)}
      id={params?.AnchorLinkTargetName}
      data-component={marketingRenderingName}
    >
      <Form data-alignment={getDroplink(rendering?.params?.Alignment)} />
    </SimpleSignup>
  );
};

export default checkDatasourceForRendering<C40SimpleSignUpProps>(Default);

export const getServerSideProps: GetServerSideComponentProps = async () => {
  return {
    formioProjectUrl: process.env.GLOBAL_FORMIO_PROJECT_URL || '',
    formioScript: process.env.GLOBAL_FORMIO_SCRIPT_URL || '',
  };
};
